import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/Icon';
import { RequireRole } from '@/components/RequireRole';
import {
  Banner,
  EmptyState,
  ErrorPage,
  LoadingPage,
  SectionTitle,
  TestModeChip,
  TriggerChip,
  WorkflowLabelChips,
} from '@/components/primitives';
import { keys, useWorkflows } from '@/hooks/useSturmData';
import { workflowsApi } from '@/lib/apiClient';
import type { WorkflowSummary } from '@/types/sturm';

export function SettingsPage() {
  const { data: workflows, isLoading, isError, error } = useWorkflows();

  if (isLoading) {
    return <LoadingPage label="Loading settings…" />;
  }

  if (isError) {
    return (
      <ErrorPage
        title="Could not load settings"
        message={error instanceof Error ? error.message : 'The API did not respond.'}
      />
    );
  }

  const disabled = (workflows ?? []).filter((w) => w.isDisabled);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Settings</div>
          <div className="page-sub">Workspace-wide settings that are not tied to a single workflow.</div>
        </div>
      </div>

      <SectionTitle count={disabled.length}>Disabled workflows</SectionTitle>
      <div className="card mb-16" style={{ overflow: 'hidden' }}>
        {disabled.length === 0 ? (
          <EmptyState
            title="No disabled workflows"
            sub="An admin can disable a workflow from its Settings tab. It then disappears from the workflow list and shows up here."
          />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Workflow</th>
                <th>Trigger</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {disabled.map((workflow) => (
                <DisabledRow key={workflow.slug} workflow={workflow} />
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Banner>
        A disabled workflow is never scheduled, cannot be started, retried or re-run, and raises no
        missed-schedule alerts. Its runs and settings stay intact; enabling it restores it to the
        workflow list exactly as it was.
      </Banner>
    </div>
  );
}

function DisabledRow({ workflow }: { workflow: WorkflowSummary }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const enable = useMutation({
    mutationFn: () => workflowsApi.enable(workflow.slug),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.workflows }),
  });

  return (
    <tr>
      <td>
        <div className="row gap-6">
          <div className="wf-name">{workflow.name}</div>
          {workflow.isTestMode ? <TestModeChip /> : null}
          <WorkflowLabelChips labels={workflow.labels} />
        </div>
        <div className="wf-queue">
          {workflow.slug} · {workflow.app} · {workflow.queue}
        </div>
        {enable.isError ? (
          <div className="tiny mt-4" style={{ color: 'var(--error)' }}>
            {enable.error instanceof Error ? enable.error.message : 'The workflow could not be enabled.'}
          </div>
        ) : null}
      </td>
      <td>
        <TriggerChip trigger={workflow.trigger} />
      </td>
      <td>
        <div className="row gap-6" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/workflows/${workflow.slug}`)}>
            <Icon name="arrowRight" size={12} /> Open
          </button>
          <RequireRole min="admin">
            <button className="btn btn-sm btn-accent" onClick={() => enable.mutate()} disabled={enable.isPending}>
              <Icon name="play" size={12} /> Enable
            </button>
          </RequireRole>
        </div>
      </td>
    </tr>
  );
}
